import {Injectable} from '@angular/core';
import {
  ProcessService
} from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/services/process/process.service';
import {Observable} from 'rxjs';
import {ENVIRONMENT} from '../../../../../environments/environment';
import {FormField} from '../../../../submodules/sellions_forms/projects/forms/src/lib/interfaces/form-field.interface';
import {
  FormVariables
} from '../../../../submodules/sellions_forms/projects/forms/src/lib/interfaces/form-variables.interface';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {ToastController} from 'ionic-angular';
import {StorageServiceProvider} from '../storage-service/storage-service';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {SimpleProcess} from '../../model/simple-process.model';
import {ProcessWithVariables} from './process-with-variables.interface';
import {
  MultipartFile
} from '../../../../submodules/sellions_forms/projects/forms/src/lib/components/controls/multipart-files/multipart-file';
import {
  ProcessWithForm
} from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/process-with-form.interface';
import {
  ObservableWorkaround
} from "../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/utils/observable-workaround.class";
import {TextServiceProvider} from "../text-service/text-service";

@Injectable()
export class ProcessWrapperService {
  readonly processFormStorePrefix = 'process-form-';
  readonly startableProcessListStoreKey = 'startableProcessList';
  readonly pendingProcessFormPrefix = 'pending-form-';
  readonly pendingProcessFormsKeysStoreKey = 'pendingProcessFormsStoreKeys';

  private pendingProcessFormsKeys: string[] = [];

  constructor(
    private httpClient: HttpClient,
    private processService: ProcessService,
    private toastController: ToastController,
    private storageService: StorageServiceProvider,
    public textService: TextServiceProvider
  ) {
    this.getPendingProcessFormKeysFromStorage();
  }

  public getStartableProcesses(processWhiteList?: string[]): Observable<SimpleProcess[]> {
    const params = this.createRequestParams(processWhiteList);
    return this.httpClient.get<SimpleProcess[]>(`${ENVIRONMENT.engineUrl}api/process/startable`, { params }).pipe(
      tap((res) => this.storeStartableProcessList(res)),
      catchError(() => this.getStartableProcessListFromStorage()),
    );
  }

  private createRequestParams(processWhiteList: string[]): HttpParams {
    if (!!processWhiteList) {
      return new HttpParams().append('processWhitelist', processWhiteList.join(', '));
    }
    return new HttpParams();
  }

  private storeStartableProcessList(startableProcessList: SimpleProcess[]): void {
    this.storageService.setObject(this.startableProcessListStoreKey, startableProcessList);
  }

  private getStartableProcessListFromStorage(): Observable<SimpleProcess[]> {
    return Observable.from(this.storageService.getObject(this.startableProcessListStoreKey)).pipe(
      tap(
        () => this.presentToast('Wystąpił problem z Twoim połączeniem internetowym. Pobrano listę dostępnych procesów z pamięci aplikacji'),
        () => this.presentToast('Wystąpił problem z Twoim połączeniem internetowym. Nie można pobrać listy dostępnych procesów'),
      ),
      catchError(() => Observable.of([])),
    );
  }

  public getProcessWithForm(processKey: string): Observable<{ form: FormField[]; formName: string }> {
    return this.processService.getProcessWithForm(ENVIRONMENT.engineUrl, processKey).pipe(
      tap((res) => this.storeProcessForm(processKey, res)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          return Observable.from(this.getProcessFormFromStorage(processKey));
        }
        if (error.error.message === 'error.accessDenied') {
          this.presentToast('Nie masz dostępu do tego procesu');
        }
        return ObservableWorkaround.throw(error);
      }),
    );
  }

  private storeProcessForm(processKey: string, res: { form: FormField[]; formName: string }): void {
    this.storageService.setObject(this.processFormStorePrefix + processKey, res);
  }

  private getProcessFormFromStorage(processKey: string): Observable<{ form: FormField[]; formName: string }> {
    return Observable.from(this.storageService.getObject(this.processFormStorePrefix + processKey)).pipe(
      tap(
        () => this.presentToast('Wystąpił problem z Twoim połączeniem internetowym. Wyświetlono formularz z pamięci aplikacji'),
        () => this.presentToast('Wystąpił problem z Twoim połączeniem internetowym. Nie można wyświetlić formularza procesu'),
      ),
    );
  }

  public initProcess(processWithForm: ProcessWithForm): Observable<any> {
    return this.processService
      .initProcess(ENVIRONMENT.engineUrl, processWithForm)
      .pipe(catchError((error) => this.handleLackOfInternetConnection(error)));
  }

  private handleLackOfInternetConnection(errorWithProcessFormAndVariables: {
    error: any;
    process: ProcessWithForm;
    variables: FormVariables;
  }): Observable<any> {
    if (errorWithProcessFormAndVariables.error.status === 0) {
      const processFormWithVariables = {
        process: errorWithProcessFormAndVariables.process,
        variables: errorWithProcessFormAndVariables.variables,
      };

      this.storePendingProcessForm(processFormWithVariables);
      this.presentToast('Wystąpił problem z Twoim połączeniem internetowym. Formularz procesu został zachowany do późniejszego przesłania');
    }

    return Observable.throw(errorWithProcessFormAndVariables.error);
  }

  private storePendingProcessForm(processWithVariables: ProcessWithVariables): void {
    const key = this.generateStoreKey();

    const files: File[] = [];

    processWithVariables.variables.multipartFiles.forEach((multipartFile) => {
      files.push(multipartFile.file);
      multipartFile.file = null; // PouchDB handles files/blobs by attachments
    });

    this.storageService.setObjectWithAttachments(key, processWithVariables, files).then(() => {
      this.pendingProcessFormsKeys.push(key);
      this.storageService.setObject(this.pendingProcessFormsKeysStoreKey, this.pendingProcessFormsKeys);
    });
  }

  private generateStoreKey(): string {
    return this.pendingProcessFormPrefix + Math.random().toString(36).substring(2, 7);
  }

  public trySendPendingProcessForms(): Observable<any> {
    this.presentToast('Rozpoczęto próbę ponownego przesłania oczekujących formularzy');

    return Observable.from(this.getPendingProcessFormKeysFromStorage()).pipe(
      mergeMap((values) => Observable.from(values)),
      mergeMap((pendingStoreKey) => Observable.from(this.getPendingProcessForm(pendingStoreKey))),
      mergeMap((formWithKey) =>
        this.processService.startProcess(ENVIRONMENT.engineUrl, formWithKey.value.process, formWithKey.value.variables).pipe(
          tap(
            () => this.removeProcessFormFromPending(formWithKey.key),
            () => this.presentToast('Wystąpił błąd podczas próby'),
          ),
        ),
      ),
      tap(() => {
        const msg = this.pendingProcessFormsKeys.length
          ? 'Pozostało: ' + this.pendingProcessFormsKeys.length
          : 'Wszystkie oczekujące formularze zostały przesłane';
        this.presentToast(`Przesłano oczekujący formularz procesu. ${msg}`);
      }),
    );
  }

  private getPendingProcessFormKeysFromStorage(): Promise<string[]> {
    return this.storageService.getObject(this.pendingProcessFormsKeysStoreKey).then(
      (val) => {
        this.pendingProcessFormsKeys = val;
        return this.pendingProcessFormsKeys;
      },
      () => {
        this.pendingProcessFormsKeys = [];
        return this.pendingProcessFormsKeys;
      },
    );
  }

  private getPendingProcessForm(key: string): Promise<{ key: string; value: ProcessWithVariables }> {
    return this.storageService.getObjectWithAttachments<ProcessWithVariables>(key).then((result) => {
      const processWithVariables = result.object;

      processWithVariables.variables.multipartFiles.forEach((multipartFile) => {
        const fileName = MultipartFile.getFileNameWithoutPath(multipartFile);
        const attachment = result.attachments[fileName];
        multipartFile.file = new File([attachment.data], fileName, { type: attachment.content_type });
      });

      return { key, value: processWithVariables };
    });
  }

  private removeProcessFormFromPending(pendingFormKey: string): void {
    const index = this.pendingProcessFormsKeys.indexOf(pendingFormKey);
    if (index > -1) {
      this.pendingProcessFormsKeys.splice(index, 1);
    }
    this.storageService
      .setObject(this.pendingProcessFormsKeysStoreKey, this.pendingProcessFormsKeys)
      .then(() => this.storageService.remove(pendingFormKey));
  }

  private presentToast(message: string): void {
    this.toastController
      .create({
        message,
        duration: 3000,
        position: 'top',
      })
      .present();
  }

  public getCountOfPendingProcessForms(): number {
    return this.pendingProcessFormsKeys.length;
  }

  public removeUserData(): void {
    this.storageService.remove(this.startableProcessListStoreKey);
    this.pendingProcessFormsKeys.forEach((key) => this.storageService.remove(key));
    this.storageService.remove(this.pendingProcessFormsKeysStoreKey).then(() => (this.pendingProcessFormsKeys = []));
  }
}



// WEBPACK FOOTER //
// ./src/modules/core/services/process-wrapper-service/process-wrapper.service.ts